// src/components/TZMMS-INV/hooks/useTZMMSInvController.js
// ============================================================================
// TZMMS Inventory Counter — primary page controller hook
// ----------------------------------------------------------------------------
// PURPOSE
// - Own all page state, scanner handling, duplicate protection, and email send.
// - Keep the page component thin and easier to edit later.
// ============================================================================

import React from 'react';

import { sendInventoryEmail } from '../api/tzmmsInvApi.js';
import {
  EMAIL_EMPTY_LIST_BLOCK,
  EMAIL_RECIPIENT_OVERRIDE,
  QR_SCANNER_ALLOW_DUPLICATE_VALUES,
  QR_SCANNER_AUTO_START_PAUSED,
  QR_SCANNER_REPEAT_BLOCK_MS,
} from '../constants/tzmmsInvUi.js';
import {
  buildInventoryEmailMessage,
  buildInventoryEmailSubject,
  buildInventoryEntry,
  normalizeScannedText,
} from '../utils/inventoryList.js';

export default function useTZMMSInvController({ userEmail }) {
  // ============================================================================
  // BLOCK TZI.CTL.001 — Primary page state
  // ============================================================================
  const [entries, setEntries] = React.useState([]); // Accepted scan rows shown in the list box.
  const [sessionNote, setSessionNote] = React.useState(''); // Free-text note shown above scan list in email.
  const [selectedEntryId, setSelectedEntryId] = React.useState(null); // Supports remove-selected action.
  const [scannerOpen, setScannerOpen] = React.useState(false); // Controls QR scanner dialog visibility.
  const [scannerPaused, setScannerPaused] = React.useState(QR_SCANNER_AUTO_START_PAUSED); // Allows pause/resume without closing.
  const [scannerError, setScannerError] = React.useState(''); // Stores last camera/scanner error message.
  const [lastScannedValue, setLastScannedValue] = React.useState(''); // Helpful session status text.
  const [emailBusy, setEmailBusy] = React.useState(false); // Prevents double-submit emails.
  const [toast, setToast] = React.useState({
    open: false,
    message: '',
    severity: 'info',
  });

  // ============================================================================
  // BLOCK TZI.CTL.002 — Duplicate scan suppression cache
  // ----------------------------------------------------------------------------
  // PURPOSE
  // - Prevent one held QR code from exploding into many rows in a second.
  // - Still allow repeated values when enough time passes or the user rescans.
  // ============================================================================
  const recentScansRef = React.useRef(new Map());
  const lastDuplicateToastRef = React.useRef(0); // Throttles the "duplicate suppressed" warning toast.
  const scannerOpenedAtRef = React.useRef(0); // Records when the scanner was last opened for warm-up guard.

  // ============================================================================
  // BLOCK TZI.CTL.003 — Small UI helpers
  // ============================================================================
  const resolvedRecipient = EMAIL_RECIPIENT_OVERRIDE || userEmail || ''; // Allows force-routing if needed.

  const showToast = React.useCallback((message, severity = 'info') => {
    setToast({ open: true, message, severity });
  }, []);

  const closeToast = React.useCallback(() => {
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  // ============================================================================
  // BLOCK TZI.CTL.004 — Scanner open / close / pause controls
  // ============================================================================
  const openScanner = React.useCallback(() => {
    recentScansRef.current.clear(); // Flush stale timing cache so no residual code fires immediately.
    scannerOpenedAtRef.current = Date.now(); // Start warm-up window to ignore camera-init ghost scans.
    setScannerError(''); // Clear stale camera errors before reopening.
    setScannerPaused(QR_SCANNER_AUTO_START_PAUSED); // Start in the default pause state from constants.
    setScannerOpen(true); // Open the dialog.
  }, []);

  const closeScanner = React.useCallback(() => {
    setScannerOpen(false); // Close the dialog after the user is done scanning.
    setScannerPaused(false); // Reset pause state so the next open starts fresh.
  }, []);

  const toggleScannerPaused = React.useCallback(() => {
    setScannerPaused((prev) => !prev); // Flip between active scan and pause.
  }, []);

  const handleScannerError = React.useCallback(
    (error) => {
      const message = String(error?.message || error || 'Unable to access camera.');
      setScannerError(message); // Keep the full message visible in the scanner dialog.
      showToast(message, 'error'); // Surface the same message to the page-level toast.
    },
    [showToast]
  );

  // ============================================================================
  // BLOCK TZI.CTL.005 — Accept scanned codes into the list
  // ----------------------------------------------------------------------------
  // IMPORTANT
  // - @yudiel/react-qr-scanner passes an ARRAY of detected codes.
  // - We accept each non-blank rawValue.
  // - We block immediate same-value repeats inside the cooldown window.
  // ============================================================================
  const handleDetectedCodes = React.useCallback(
    (detectedCodes) => {
      const safeCodes = Array.isArray(detectedCodes) ? detectedCodes : [];
      const now = Date.now();
      const acceptedEntries = [];
      let blockedCount = 0;

      // Ignore any scan events fired during the camera warm-up window (first 800 ms after open).
      // This prevents stale camera frames or residual data from being accepted on dialog open.
      if (now - scannerOpenedAtRef.current < 800) {
        return;
      }

      safeCodes.forEach((detectedCode) => {
        const normalizedValue = normalizeScannedText(detectedCode?.rawValue);

        if (!normalizedValue) {
          return; // Ignore empty or whitespace-only QR values.
        }

        const mostRecentSameValueAt = recentScansRef.current.get(normalizedValue) || 0;
        const scannedTooSoon = now - mostRecentSameValueAt < QR_SCANNER_REPEAT_BLOCK_MS;

        if (scannedTooSoon) {
          blockedCount++;
          return; // Prevent one held QR code from creating many rows instantly.
        }

        if (!QR_SCANNER_ALLOW_DUPLICATE_VALUES) {
          const alreadyExists = entries.some((entry) => entry.rawValue === normalizedValue);
          if (alreadyExists) {
            blockedCount++;
            return; // Optional full-session dedupe mode when constants disable duplicates.
          }
        }

        recentScansRef.current.set(normalizedValue, now); // Remember accepted value timestamp.
        acceptedEntries.push(buildInventoryEntry(normalizedValue, new Date(now))); // Build row once accepted.
      });

      if (!acceptedEntries.length) {
        // Warn the user (at most once every 3 s) that a duplicate was caught and suppressed.
        if (blockedCount > 0 && now - lastDuplicateToastRef.current >= 3000) {
          lastDuplicateToastRef.current = now;
          showToast('Duplicate scan suppressed — move to the next item.', 'warning');
        }
        return;
      }

      setEntries((prev) => [...prev, ...acceptedEntries]); // Append new rows to the list box.
      setSelectedEntryId(acceptedEntries[acceptedEntries.length - 1].id); // Select the newest added row.
      setLastScannedValue(acceptedEntries[acceptedEntries.length - 1].rawValue); // Show accepted last scan.
      setScannerError(''); // Clear any stale camera error after successful scanning.
      showToast(
        `Added ${acceptedEntries.length} item${acceptedEntries.length === 1 ? '' : 's'} from scan.`,
        'success'
      );
    },
    [entries, showToast]
  );

  // ============================================================================
  // BLOCK TZI.CTL.006 — Row editing actions
  // ============================================================================
  const removeSelectedEntry = React.useCallback(() => {
    if (!selectedEntryId) {
      return; // No selected row means there is nothing to remove.
    }

    setEntries((prev) => prev.filter((entry) => entry.id !== selectedEntryId)); // Remove selected row only.
    setSelectedEntryId(null); // Clear selection after deletion so UI state stays obvious.
    showToast('Selected row removed.', 'info');
  }, [selectedEntryId, showToast]);

  const clearAllEntries = React.useCallback(() => {
    setEntries([]); // Reset the session list completely.
    setSelectedEntryId(null); // Reset selected row at the same time.
    setLastScannedValue(''); // Clear summary display as part of session reset.
    recentScansRef.current.clear(); // Clear recent duplicate-block cache too.
    showToast('All scanned rows cleared.', 'warning');
  }, [showToast]);

  // ============================================================================
  // BLOCK TZI.CTL.007 — Email send action
  // ============================================================================
  const emailContents = React.useCallback(async () => {
    if (EMAIL_EMPTY_LIST_BLOCK && !entries.length) {
      showToast('There are no scanned rows to email.', 'warning');
      return;
    }

    if (!resolvedRecipient) {
      showToast('No recipient email was provided to the page.', 'error');
      return;
    }

    setEmailBusy(true); // Disable the button until the current email attempt finishes.

    try {
      const subject = buildInventoryEmailSubject(entries); // Build consistent subject text.
      const message = buildInventoryEmailMessage(entries, new Date(), sessionNote); // Prepend note if present.
      const result = await sendInventoryEmail({ to: resolvedRecipient, subject, message });

      if (!result.success) {
        throw new Error(result.error || 'Failed to send inventory email.');
      }

      showToast(`Inventory email sent to ${resolvedRecipient}.`, 'success');
    } catch (error) {
      showToast(String(error?.message || error || 'Failed to send inventory email.'), 'error');
    } finally {
      setEmailBusy(false); // Always re-enable the action button.
    }
  }, [entries, resolvedRecipient, sessionNote, showToast]);

  // ============================================================================
  // BLOCK TZI.CTL.008 — Public hook contract
  // ============================================================================
  return {
    entries,
    sessionNote,
    setSessionNote,
    selectedEntryId,
    scannerOpen,
    scannerPaused,
    scannerError,
    lastScannedValue,
    emailBusy,
    toast,
    resolvedRecipient,
    setSelectedEntryId,
    openScanner,
    closeScanner,
    toggleScannerPaused,
    handleScannerError,
    handleDetectedCodes,
    removeSelectedEntry,
    clearAllEntries,
    closeToast,
    emailContents,
  };
}
